import omit from 'lodash/omit';

import { BadStatusError } from '@confluence/network';
import { MediaType } from '@confluence/graphql/entry-points/MediaType';
import { getEditDraftPageUrl } from '@confluence/graphql/entry-points/sharedTemplateBlueprintReqFns';
import { useExperimentalMutation } from '@confluence/legacy-schema-bridge/entry-points/useExperimentalMutation';

import type {
	CreatePageByBlueprintMutation,
	CreatePageByBlueprintMutationVariables,
} from '../queries/__types__/CreatePageByBlueprintMutation';

const MEETING_NOTES_KEY =
	'com.atlassian.confluence.plugins.confluence-business-blueprints:meeting-notes-item';

const isMeetingNote = ({ itemModuleCompleteKey }: any) =>
	itemModuleCompleteKey === MEETING_NOTES_KEY;

// omit certain GraphQL attributes from being sent to /content/blueprint/:blueprintId/instance
const processData = (blueprint: any) => omit(blueprint, ['__typename', 'wizard']);

export const useExperimentalPublishPageByBlueprintMutation = () => {
	return useExperimentalMutation<
		CreatePageByBlueprintMutation,
		CreatePageByBlueprintMutationVariables,
		CreatePageByBlueprintMutationResponse
	>({
		url: (variables) => {
			let blueprintSpec: any;
			try {
				blueprintSpec = processData(JSON.parse(variables.blueprint));
			} catch (err) {
				throw new Error('blueprint JSON passed to experimentalPublishPageByBlueprint is invalid');
			}

			return `/wiki/rest/create-dialog/1.0/content/blueprint/${
				blueprintSpec.itemModuleCompleteKey
			}/instance${variables.attemptSpaUrlResponse ? '?expand=metadata.properties.editor,space' : ''}`;
		},
		method: 'POST',
		headers: {
			'Content-Type': MediaType.JSON,
		},
		body: ({ title, spaceKey, blueprint, parentPageId: contentId, subType }) => {
			let blueprintSpec: any;
			try {
				blueprintSpec = processData(JSON.parse(blueprint));
			} catch (err) {
				throw new Error('blueprint JSON passed to experimentalPublishPageByBlueprint is invalid');
			}

			const commonContentProperties: any = {
				title,
				type: 'page',
				status: 'draft',
				space: {
					key: spaceKey,
				},
			};

			if (contentId && contentId !== 'no-parent-page') {
				commonContentProperties.ancestors = [{ id: contentId }];
			}

			if (subType) {
				commonContentProperties.subType = subType;
			}

			// add spaceKey so there is no infinite spinner when creating page with meeting notes
			if (isMeetingNote(blueprintSpec)) {
				blueprintSpec.spaceKey = spaceKey;
			}

			return JSON.stringify({
				content: {
					...commonContentProperties,
				},
				contentBlueprintSpec: {
					blueprintId: blueprintSpec.contentBlueprintId,
					context: blueprintSpec,
				},
			});
		},
		parseResult: async (json, { attemptSpaUrlResponse = false }) => {
			const editui = await getEditDraftPageUrl(json, attemptSpaUrlResponse);

			if (!editui) {
				throw new Error('Missing content draft url');
			}

			return {
				experimentalPublishPageByBlueprint: {
					links: {
						draftUrl: editui,
					},
				},
			};
		},
		onError: async (error) => {
			if (error instanceof BadStatusError && error.response?.status === 500) {
				const backendError = await error.response.json();
				throw new BadStatusError(backendError.errorMessage, error.response);
			}
			throw error;
		},
	});
};

interface CreatePageByBlueprintMutationResponse {
	content: {
		_links: {
			editui: string;
		};
		metadata?: {
			properties?: {
				editor?: {
					value: string;
				};
			};
		};
		subType?: string;
	};
}
